import { defineMeta } from '@/registry/types';

export const chipGroupMeta = defineMeta({
  name: 'ChipGroup',
  level: 'molecule',
  category: 'data-display',
  classification: 'custom',
  description:
    'Layout and overflow for a collection of Chips. Wraps on the 8px grid, caps the visible count and discloses the remainder through a single `+N more` control.',
  baseLibrary: 'react',
  importPath: '@/molecules/ChipGroup',
  requiredProps: ['children'],
  optionalProps: ['max', 'spacing', 'noWrap', 'onOverflow', 'label', 'size'],
  supportedStates: ['default', 'capped', 'expanded'],
  designTokens: ['spacing'],
  accessibility: [
    'Pass `label` to expose the group as a named `group` landmark; without it the group is presentational.',
    'The overflow control is a button carrying aria-expanded, so keyboard users can reveal the remainder.',
  ],
  usageExamples: [
    '<ChipGroup label="Techniques" max={3}>{items.map((t) => <Chip key={t} purpose="category" label={t} size="small" />)}</ChipGroup>',
    '<ChipGroup max={2} onOverflow={openDetailPanel}>{chips}</ChipGroup>',
  ],
  rules: [
    'Use ChipGroup whenever more than two chips sit together; it owns the spacing so children never set their own margins.',
    'Set `max` in tables and dense cells so a long list cannot change row height; leave it unset in detail views where wrapping is fine.',
    'Keep groups to six chips on one line, and wrapping to at most five lines. Past that use a multi-select or a table instead.',
    'Pass `onOverflow` when the remainder belongs in a popover or detail view; the default expands in place.',
    'Give the group a `label` when the chips share a dimension, so assistive technology can announce what the collection is.',
    'Pass `size` so the overflow `+N more` chip matches the group. Children keep their own `size` — set the same value on both.',
    'A ChipGroup of `purpose="select"` chips is layout, not a selection model. Each child stays independently controlled; exclusive grouping lives in the parent.',
  ],
  antiPatterns: [
    'Do not set margins on the chips themselves; the group owns spacing via `gap`.',
    'Do not mix chip purposes in one group — severity beside category reads as one dimension.',
    'Do not render your own `+N more` element; use `max` so the count and the aria-expanded state stay correct.',
  ],
  llmSafe: true,
  whenToUse: ['Collections of tags, techniques, data sources, applied filters, or toggleable options'],
  whenNotToUse: [
    'A single chip (render it directly)',
    'Selecting from many options (use Autocomplete or FilterPanel)',
  ],
});

export default chipGroupMeta;
